import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import dialog from '../../versa/dialog.js';
import filter, { resolveFilterLabelsToIds } from '../../versa/filter.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import { flatten, deepen } from '../../versa/record.js';
import { notify, ask } from '../../versa/msgbox.js';
import { splitIdsByCompany } from '../../versa/misc.js';
import pdf from '../../versa/pdf.js';
import view from '../views/output-orders.js';
import form from '../forms/output-orders.js';
import { outputOrderItemsHaveMixedStorageArm } from '../forms/output-orders-storage-arm.js';
import { storageArmCodeFromProduct, storageArmLabel } from '../utils/storage-arm-type.js';
import output_stats_view from '../views/output-orders-output-stats.js';
import create_outputs_form from '../forms/output-orders-create-outputs.js';
import upload_form from '../forms/output-orders-upload.js';
import order_upload_result_view from '../views/order-upload-result.js';
import print_pending_form from '../forms/output-orders-print-pending.js';
import print_mirror_form from '../forms/output-orders-print-mirror.js';
import print_durability_form from '../forms/stocks-print-durability.js';
import export_csv_form from '../forms/output-orders-export-csv.js';
import record from '../../versa/record.js';
import * as types from '../types.js';
import { referenceSearchPayloadFromQuery } from '../utils/bulk-reference-search.js';

function downloadFile(base64Content, filename, mimeType) {
	const bin = atob(base64Content);
	const arr = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
	const blob = new Blob([arr], { type: mimeType });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename || 'download';
	a.click();
	URL.revokeObjectURL(url);
}

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({
	'&': '&amp;',
	'<': '&lt;',
	'>': '&gt;',
	'"': '&quot;',
	"'": '&#39;'
})[ch]);

const outputOrders = function()
{

	let output_orders_crud = crud.create({
		async loadBaseData()
		{
			api.call(api.list_companies, this.queryAll()).then((data) => db.set('companies', data).catch(error => notify(error)));
			api.call(api.list_programs, this.queryAll()).then((data) => db.set('programs', data).catch(() => {}));
			api.call(api.list_warehouses, this.queryAll()).then((data) => db.set('warehouses', data).catch(error => notify(error)));
			api.call(api.list_units, this.queryAll()).then((data) => db.set('units', data).catch(error => notify(error)));
			api.call(api.list_products, this.queryAll()).then((data) => db.set('products', data).catch(error => notify(error)));
			api.call(api.list_lots, this.queryAll()).then((data) => db.set('lots', data).catch(error => notify(error)));
			api.call(api.list_price_lists, this.queryAll()).then((data) => db.set('price_lists', data).catch(error => notify(error)));
			api.call(api.list_payment_terms, this.queryAll()).then((data) => db.set('payment_terms', data).catch(error => notify(error)));
			api.call(api.list_customers, this.queryAll()).then((data) => db.set('customers', data).catch(error => notify(error)));
			api.call(api.list_customer_routes, this.queryAll()).then((data) => db.set('customer_routes', data).catch(() => {}));
			api.call(api.list_carriers, this.queryAll()).then((data) => db.set('carriers', data).catch(error => notify(error)));
			api.call(api.list_users, this.queryAll()).then((data) => db.set('users', data).catch(error => notify(error)));
		},
		async queryData(query)
		{
			const searchPayload = referenceSearchPayloadFromQuery(this.filter, query);
			let records = searchPayload
				? await api.call(api.search_output_orders, searchPayload)
				: await api.call(api.list_output_orders, query);
			if (!Array.isArray(records) || records.length === 0)
				return records;
			// #MT-6D9A723 — enrich pré-reserva / saldo simulado (não trava a grade se falhar)
			try
			{
				const ids = records
					.filter(r => r && r.company_id != null && r.id != null)
					.map(r => [r.company_id, r.id]);
				if (ids.length === 0)
					return records;
				const summary = await api.call(api.summarize_output_orders_simulated_availability, { ids });
				if (!summary || typeof summary !== 'object')
					return records;
				for (const r of records)
				{
					const key = `${r.company_id}:${r.id}`;
					const s = summary[key];
					if (!s || s.applicable === false)
					{
						r.pre_reserved_pending = null;
						r.simulated_available_quantity = null;
						r.stock_situation = null;
						continue;
					}
					r.pre_reserved_pending = s.pre_reserved_pending;
					r.operational_available_quantity = s.operational_available_quantity;
					r.simulated_available_quantity = s.simulated_available_quantity;
					r.stock_situation = s.stock_situation;
				}
			}
			catch (_e)
			{
				/* grade segue sem colunas de simulação */
			}
			return records;
		},
		async loadRecord(id)
		{
			let _record = await api.call(api.select_output_order, ...JSON.parse(id));
			// #MT-6D9A723 — enriquecer itens com pré-reserva / saldo simulado (detalhe)
			// #MT-F0A7552 — pedido Devolução não participa; não enriquecer colunas
			// (sempre flatten: sem isso os itens não aparecem no formulário)
			try
			{
				const items = Array.isArray(_record?.items) ? _record.items : [];
				if (_record?.company_id && items.length)
				{
					for (const it of items)
					{
						const pid = Number(it?.product_id);
						if (!Number.isInteger(pid) || pid <= 0)
						{
							it.storage_arm = '';
							continue;
						}
						let product = null;
						try
						{
							product = await db.get('products', [_record.company_id, pid]);
						}
						catch (_e)
						{
							product = null;
						}
						it.storage_arm = storageArmLabel(
							storageArmCodeFromProduct(product || { is_refrigerated: false }),
						);
					}
				}
				if (!_record?.is_return)
				{
					const productIds = [...new Set(
						items
							.map(it => Number(it?.product_id))
							.filter(pid => Number.isInteger(pid) && pid > 0)
					)];
					if (_record?.company_id && productIds.length)
					{
						const q = `?product_ids=${productIds.join(',')}&include_zero_stock=1&per_page=${Math.max(100, productIds.length)}`;
						const avail = await api.call(api.list_stocks_simulated_availability, _record.company_id, q);
						const byPid = new Map();
						for (const row of (avail?.items || []))
						{
							if (row?.product_id != null)
								byPid.set(Number(row.product_id), row);
						}
						for (const it of items)
						{
							const row = byPid.get(Number(it.product_id));
							if (!row)
							{
								it.pre_reserved_by_approved_orders = null;
								it.simulated_available_quantity = null;
								it.stock_situation = null;
								continue;
							}
							it.pre_reserved_by_approved_orders = row.pre_reserved_by_approved_orders;
							it.simulated_available_quantity = row.simulated_available_quantity;
							it.stock_situation = row.status || null;
						}
					}
				}
			}
			catch (_e)
			{
				/* detalhe segue sem colunas de simulação */
			}
			return flatten(_record, 'items');
		},
		async saveRecord(data, old_data)
		{
			data = deepen(data, 'items');
			let _record = {
				reference: data.reference,
				customer_id: Number(data.customer_id),
				delivery_address_id: (data.delivery_address_id != null && data.delivery_address_id !== '' && !isNaN(Number(data.delivery_address_id)))
					? Number(data.delivery_address_id)
					: null,
				carrier_id: Number(data.carrier_id),
				program_id: data.program_id != null && data.program_id !== '' ? Number(data.program_id) : null,
				service_channel: data.service_channel,
				payment_method: data.payment_method,
				payment_term_id: (data.payment_method === '90' || data.payment_method === 90) && (data.payment_term_id == null || data.payment_term_id === '')
					? null
					: Number(data.payment_term_id),
				freight_type: data.freight_type,
				freight_value: Number(data.freight_value),
				discount: (data.discount != null && data.discount !== '') ? Number(data.discount) : null,
				notes: data.notes?.trim(),
				book_observation: (data.book_observation != null && String(data.book_observation).trim() !== '')
					? String(data.book_observation).trim()
					: null,
				is_return: data.is_return == '1',	// Can be: undefined, '0', '1'
				is_emergency: data.is_emergency == '1',
				status: Number(data.status),
				expected_delivery_date: (data.expected_delivery_date && data.expected_delivery_date !== '') ? data.expected_delivery_date : null,
				items: (data.items || [])
					.filter(item => item.product_id != null && item.product_id !== '' && !isNaN(Number(item.product_id)))
					.map(item => ({
						product_id: Number(item.product_id),
						lot_id: (item.lot_id != null && item.lot_id !== '' && !isNaN(Number(item.lot_id)) && Number(item.lot_id) >= 0) ? Number(item.lot_id) : null,
						unit_id: Number(item.unit_id),
						quantity: Number(item.quantity),
						discount: (item.discount != null && item.discount !== '') ? Number(item.discount) : null,
						unit_price: Number(item.unit_price)
					}))
			};
			// #MT-3902248 — mistura Climatizado × Refrigerado: alerta + confirmação (não bloqueia)
			if (data.company_id && await outputOrderItemsHaveMixedStorageArm(data.company_id, _record.items))
			{
				const ok = await new Promise((resolve) =>
				{
					ask(
						'Este pedido mistura itens Climatizados e Refrigerados.\n\nSepare em pedidos distintos antes de gerar a saída, ou confirme se deseja salvar mesmo assim.',
						'Mistura de armazenamento',
						(answer) => resolve(answer === 'Y'),
					);
				});
				if (!ok)
					return false;
			}
			let resp;
			if (old_data)
			{
				// Alerta ao marcar como Concluído (40) quando ainda há pendências: altera como o pedido é informado no BI
				if (Number(data.status) === 40)
				{
					const pending = await api.call(api.select_pending_output_items, data.company_id, data.id);
					if (Array.isArray(pending) && pending.some(item => (item.quantity_needed || 0) > 0))
						notify('Este pedido não foi totalmente atendido. Marcar como Concluído altera o formato como o pedido é informado no BI.', 'Atenção');
				}
				resp = await api.call(api.update_output_order, data.company_id, data.id, _record);
			}
			else
				resp = await api.call(api.insert_output_order, data.company_id, _record);
			const dup = resp?.duplicate_reference;
			if (dup) {
				const orderId = typeof dup === 'object' && dup != null && dup.id != null ? dup.id : dup;
				const company_nick = typeof dup === 'object' && dup != null ? dup.company_nick : null;
				const tenant = company_nick ? ` (empresa ${company_nick})` : '';
				notify(
					`Já existe o pedido de saída nº ${orderId}${tenant} com a mesma referência. Altere a referência e salve novamente; nada foi gravado.`,
					'Atenção',
				);
				return false;
			}
			return resp;
		},
		editFilter() {
			filter.edit(this.applyFilter.bind(this), this.filter);
		},
		async applyFilter(data) {
			this.filter = await resolveFilterLabelsToIds(data, this.fieldspecs);
			this.loadPage(1);
		},
		createOutputs()
		{
			if (!this.ensureSelection('Gerar Saídas'))
				return;
			let createOutputsInFlight = false;
			const showResult = async (results) =>
			{
				if (results == null || results === undefined)
					return;	// Erro já tratado pelo http/notify
				notify(await output_stats_view(results), 'Resultados');
				this.loadPage();	// Show updated order status
				fs.destroy();
			}
			const sendRequest = async (data) =>
			{
				if (createOutputsInFlight)
					return;
				createOutputsInFlight = true;
				try
				{
					const parsedWarehouseId = Number(data.warehouse_id);
					let params = {
						'warehouse_company_id': data.company_id,
						'warehouse_id': Number.isInteger(parsedWarehouseId) && parsedWarehouseId > 0 ? parsedWarehouseId : null,
						'reserve_flag': Boolean(data?.reserve_flag),
						'include_expired': Boolean(data?.include_expired),
						'ids': await this.getSelectedIds(['company_id', 'id'])
					};
					let resp = await api.call(api.create_outputs, params);
					if (resp != null)
						await showResult(resp);
				}
				finally
				{
					createOutputsInFlight = false;
				}
			}
			let fs = formset.create(create_outputs_form, sendRequest);
			fs.init = (() =>
			{
				document.querySelector(`${fs.form.selector} #count`).innerHTML = this.getCount();
				Object.getPrototypeOf(fs).init.call(fs);
			})();
		},
		uploadFromSpreadsheet()
		{
			const showResult = (result) =>
			{
				notify(order_upload_result_view(result, 'output'), 'Resultado do upload');
				this.loadPage();
				fs.destroy();
			};
			const sendRequest = async (data) =>
			{
				const formNode = document.querySelector(upload_form.form.selector);
				const fileInput = formNode?.querySelector('#file');
				const file = fileInput?.files?.[0];
				if (!file)
				{
					notify('Selecione um arquivo CSV ou XLSX.');
					return;
				}
				const company_id = data.company_id;
				if (!company_id)
				{
					notify('Selecione a empresa.');
					return;
				}
				const fd = new FormData();
				fd.append('file', file);
				fd.append('customer_id', data.customer_id);
				fd.append('program_id', data.program_id || '');
				const result = await api.call(api.upload_output_order, company_id, fd);
				if (result)
					showResult(result);
			};
			const setupTemplateLink = (fs) =>
			{
				const link = fs.dialog.getNode().querySelector('#download-template');
				if (link)
				{
					link.onclick = async (e) =>
					{
						e.preventDefault();
						const company_id = fs.form.getValues().company_id;
						if (!company_id)
						{
							notify('Selecione a empresa para baixar o modelo.');
							return;
						}
						const csv = await api.call(api.order_upload_template, company_id);
						if (csv && typeof csv === 'string')
						{
							const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
							const a = document.createElement('a');
							a.href = URL.createObjectURL(blob);
							a.download = 'modelo_pedido_upload.csv';
							a.click();
							URL.revokeObjectURL(a.href);
						}
					};
				}
			};
			const fs = formset.create(upload_form, sendRequest);
			fs.init = (() =>
			{
				Object.getPrototypeOf(fs).init.call(fs);
				setupTemplateLink(fs);
			})();
		},
		async printPendingOrders()
		{
			const crud = this;
			const ids = splitIdsByCompany(await crud.getSelectedIds(['company_id', 'id']));
			const count = Object.values(ids).reduce((s, arr) => s + (arr?.length || 0), 0);
			if (!count) {
				notify('Selecione ao menos um pedido para gerar o relatório.', 'Relatório de Pendências');
				return;
			}
			record.edit(print_pending_form, async (data) =>
			{
				try {
					const resp = await api.call(api.print_pending_output_orders, { ids, format: data.format || 'pdf' });
					crud.clearGridSelection();
					if (resp?.pdf)
						pdf(resp.pdf);
					else if (resp?.csv) {
						downloadFile(resp.csv, resp.filename || 'pendencias.csv', 'text/csv;charset=utf-8');
						notify('Download iniciado.', 'Sucesso');
					} else if (resp?.xlsx) {
						downloadFile(resp.xlsx, resp.filename || 'pendencias.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
						notify('Download iniciado.', 'Sucesso');
					} else
						notify('Nenhum dado para o relatório.', 'Aviso');
				} catch (e) {
					notify(e?.message || String(e), 'Erro');
				}
			}, {}, false);
		},
		printOrderDocument()
		{
			const count = this.getCount();
			if (!count) {
				notify('Selecione ao menos um pedido para gerar o espelho.', 'Espelho de Pedido');
				return;
			}
			record.edit(print_mirror_form, async (data) =>
			{
				try {
					const ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
					const resp = await api.call(api.print_output_order_document, { ids, format: data.format || 'pdf' });
					this.clearGridSelection();
					if (resp?.pdf)
						pdf(resp.pdf);
					else if (resp?.csv) {
						downloadFile(resp.csv, resp.filename || 'espelho-pedido-saida.csv', 'text/csv;charset=utf-8');
						notify('Download iniciado.', 'Sucesso');
					} else if (resp?.xlsx) {
						downloadFile(resp.xlsx, resp.filename || 'espelho-pedido-saida.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
						notify('Download iniciado.', 'Sucesso');
					} else
						notify('Nenhum dado para o espelho.', 'Aviso');
				} catch (e) {
					notify(e?.message || String(e), 'Erro');
				}
			}, {}, false);
		},
		printDurability()
		{
			const fs = formset.create(print_durability_form, async (data) =>
			{
				if (!data.company_id) {
					notify('Selecione a empresa.', 'Atenção');
					return false;
				}
				const payload = {
					company_id: data.company_id,
					warehouse_id: data.warehouse_id ? Number(data.warehouse_id) : null,
					program_id: data.program_id ? Number(data.program_id) : null,
					only_negative: Boolean(data?.only_negative),
				};
				try {
					const json = await api.call(api.print_stock_durability, payload);
					if (json?.xlsx) {
						downloadFile(
							json.xlsx,
							json.filename || 'durabilidade-estoque.xlsx',
							'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
						);
						notify('Download do Excel iniciado.', 'Sucesso');
						fs.destroy();
					} else {
						notify('Nenhum dado para exportar.', 'Aviso');
					}
				} catch (e) {
					notify(e?.message || String(e), 'Erro');
				}
				return false;
			});
			fs.init();
		},
		async approveOrders()
		{
			const selectedIds = await this.getSelectedIds(['company_id', 'id']);
			if (!selectedIds?.length) {
				notify('Selecione ao menos um pedido para aprovar.', 'Aprovar Selecionados');
				return;
			}

			// Agrupa por empresa (pedidos de empresas diferentes vão em chamadas separadas)
			const byCompany = {};
			for (const pair of selectedIds) {
				const [company_id, id] = pair;
				if (!byCompany[company_id]) byCompany[company_id] = [];
				byCompany[company_id].push(id);
			}
			const totalOrders = selectedIds.length;

			const html = `
				<div class="msgbox" style="min-width:340px;max-width:500px">
					<div class="mb-title">Aprovar Pedidos de Saída</div>
					<div class="mb-text">
						<p style="margin:0 0 0.75rem">
							Confirma a aprovação de <strong>${totalOrders} pedido${totalOrders !== 1 ? 's' : ''}</strong> selecionado${totalOrders !== 1 ? 's' : ''}?
						</p>
						<p style="margin:0;color:#888;font-size:0.92em">
							Apenas pedidos com status <strong>Criado</strong> serão aprovados.
							Pedidos em outros status serão ignorados.
						</p>
					</div>
					<button type="button" class="button mtl bn_N">Cancelar</button>
					<button type="button" class="button mtl bn_Y">Aprovar</button>
				</div>
			`;

			const crudRef = this;
			const dlg = dialog.create(html, async (key) => {
				if (key === 'Escape' || key === 'N') {
					dlg.destroy();
					return;
				}
				if (key === 'Y') {
					dlg.destroy();
					try {
						let totalApproved = 0;
						let totalSkipped = 0;
						for (const [company_id, ids] of Object.entries(byCompany)) {
							const result = await api.call(api.approve_output_orders_batch, company_id, { ids });
							totalApproved += result?.approved || 0;
							totalSkipped += result?.skipped || 0;
						}
						if (totalApproved > 0) {
							notify(
								`${totalApproved} pedido${totalApproved !== 1 ? 's' : ''} aprovado${totalApproved !== 1 ? 's' : ''} com sucesso.` +
								(totalSkipped > 0 ? ` ${totalSkipped} ignorado${totalSkipped !== 1 ? 's' : ''} (status diferente de Criado).` : ''),
								'Aprovação em lote'
							);
							crudRef.loadPage();
						} else {
							notify('Nenhum pedido foi aprovado. Verifique se os pedidos selecionados estão com status Criado.', 'Aprovação em lote');
						}
					} catch (e) {
						notify(e?.message || String(e), 'Erro ao aprovar pedidos');
					}
				}
			}, /Escape|N|Y/);
		},

		async createPendingOrder()
		{
			const selectedIds = await this.getSelectedIds(['company_id', 'id']);
			if (!selectedIds?.length) {
				notify('Selecione um pedido parcialmente atendido.', 'Pedido complementar');
				return;
			}
			if (selectedIds.length !== 1) {
				notify('Selecione apenas um pedido por vez para gerar complemento.', 'Pedido complementar');
				return;
			}
			const [company_id, id] = selectedIds[0];
			try {
				const info = await api.call(api.output_order_pending_eligibility, company_id, id);
				if (!info?.eligible) {
					notify(info?.reason || 'Pedido não elegível para complemento.', 'Pedido complementar');
					return;
				}
				const items = Array.isArray(info.pending_items) ? info.pending_items : [];
				const qty = items.reduce((sum, item) => sum + Number(item.quantity_needed || 0), 0);
				const ref = escapeHtml(info.reference);
				const html = `
					<div class="msgbox" style="min-width:360px;max-width:560px">
						<div class="mb-title">Gerar pedido complementar</div>
						<div class="mb-text">
							<p style="margin:0 0 0.75rem">Confirma gerar o pedido <strong>${ref}</strong>?</p>
							<p style="margin:0;color:#666">Itens pendentes: <strong>${items.length}</strong> &nbsp; Quantidade total: <strong>${qty}</strong></p>
							<p style="margin:0.75rem 0 0;color:#888;font-size:0.92em">O pedido complementar não gera saída nem reserva estoque automaticamente.</p>
						</div>
						<button type="button" class="button mtl bn_N">Cancelar</button>
						<button type="button" class="button mtl bn_Y">Gerar</button>
					</div>
				`;
				const crudRef = this;
				const dlg = dialog.create(html, async (key) => {
					if (key === 'Escape' || key === 'N') {
						dlg.destroy();
						return;
					}
					if (key === 'Y') {
						dlg.destroy();
						try {
							const resp = await api.call(api.create_pending_output_order, company_id, id);
							notify(`Pedido complementar ${resp?.reference || ''} gerado com sucesso.`, 'Pedido complementar');
							crudRef.clearGridSelection();
							crudRef.loadPage();
						} catch (e) {
							notify(e?.message || String(e), 'Erro ao gerar complemento');
						}
					}
				}, /Escape|N|Y/);
			} catch (e) {
				notify(e?.message || String(e), 'Pedido complementar');
			}
		},

		async markPartialCompleted()
		{
			const selectedIds = await this.getSelectedIds(['company_id', 'id']);
			if (!selectedIds?.length) {
				notify('Selecione um pedido parcialmente atendido.', 'Concluir parcial');
				return;
			}
			if (selectedIds.length !== 1) {
				notify('Selecione apenas um pedido por vez.', 'Concluir parcial');
				return;
			}
			const [company_id, id] = selectedIds[0];
			const html = `
				<div class="msgbox" style="min-width:360px;max-width:560px">
					<div class="mb-title">Concluir parcial</div>
					<div class="mb-text">
						<p style="margin:0 0 0.75rem">Confirma concluir parcialmente o pedido <strong>#${id}</strong>?</p>
						<p style="margin:0;color:#666">O status passará para <strong>Concluído parcialmente</strong>. Não será possível gerar pedido complementar nem nova saída neste pedido.</p>
					</div>
					<button type="button" class="button mtl bn_N">Cancelar</button>
					<button type="button" class="button mtl bn_Y">Confirmar</button>
				</div>
			`;
			const crudRef = this;
			const dlg = dialog.create(html, async (key) => {
				if (key === 'Escape' || key === 'N') {
					dlg.destroy();
					return;
				}
				if (key === 'Y') {
					dlg.destroy();
					try {
						await api.call(api.mark_output_order_partial_completed, company_id, id);
						notify('Pedido concluído parcialmente.', 'Concluir parcial');
						crudRef.clearGridSelection();
						crudRef.loadPage();
					} catch (e) {
						notify(e?.message || String(e), 'Erro ao concluir parcial');
					}
				}
			}, /Escape|N|Y/);
		},

		async exportListCsv()
		{
			const crud = this;
			await record.edit(export_csv_form, async () =>
			{
				const box = document.querySelector(`${export_csv_form.form.selector} #use_list_filter`);
				const useList = box ? box.checked : true;
				const payload = {};
				if (useList)
				{
					const params = new URLSearchParams();
					await crud.addFilter(params);
					crud.addSort(params);
					const f = params.get('filter');
					const s = params.get('sort');
					if (f)
						payload.filter = f;
					if (s)
						payload.sort = s;
				}
				try
				{
					const resp = await api.call(api.export_output_orders_csv, payload);
					if (resp?.csv)
					{
						downloadFile(resp.csv, resp.filename || 'pedidos-saida.csv', 'text/csv;charset=utf-8');
						notify('Download iniciado.', 'Exportar listagem (CSV)');
					}
					else
						notify('Não foi possível gerar o arquivo.', 'Aviso');
				}
				catch (e)
				{
					notify(e?.message || String(e), 'Erro');
				}
			}, {}, false);
		},
	});
	
	// Index = position in the backend's SELECT
	let fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id', label: 'Pedido', types: [types.number(0, 2)], placeholder: '0', title: 'Identificador do pedido' },
		{ index: 3, field: 'reference', label: 'Referência', types: [], placeholder: '', title: 'Código de referência externa do pedido' },
		{ index: 4, field: 'created_on', label: 'Data Criação', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data e hora de criação do pedido' },
		{ index: 5, field: 'created_by', label: 'Criado Por', types: [types.datalist()], placeholder: '', title: 'Autor da criação do pedido', store: 'users', labels: ['name'] },
		{ index: 6, field: 'updated_on', label: 'Data Alteração', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data e hora da última atualização do pedido' },
		{ index: 7, field: 'updated_by', label: 'Alterado Por', types: [types.datalist()], placeholder: '', title: 'Autor da última atualização do pedido', store: 'users', labels: ['name'] },
		{ index: 8, field: 'customer_id', label: 'Cliente', types: [types.datalist()], placeholder: 'Apelido, id ou CNPJ/CPF', title: 'Cliente (apelido, código ou documento CNPJ/CPF)', store: 'customers', labels: ['nick'] },
		{ index: 30, field: 'route_id', label: 'Rota', types: [types.datalist()], placeholder: '', title: 'Rota de entrega do cliente', store: 'customer_routes', labels: ['code', 'name'] },
		{ index: 9, field: 'carrier_id', label: 'Transportador', types: [types.datalist()], placeholder: '', title: 'Transportador', store: 'carriers', labels: ['nick'] },
		{ index: 10, field: 'program_id', label: 'Programa', types: [types.datalist()], placeholder: '', title: 'Programa (estrutura de estoque)', store: 'programs', labels: ['name'] },
		{ index: 11, field: 'service_channel', label: 'Atendimento',	types: [types.opt_service_channel(), types.datalist()], placeholder: '', title: 'Canal de atendimento' },
		{ index: 12, field: 'payment_method', label: 'Pagamento', types: [types.opt_payment_method(), types.datalist()], placeholder: '', title: 'Forma de pagamento' },
		{ index: 13, field: 'payment_term_id', label: 'Condição', types: [types.datalist()], placeholder: '', title: 'Condição de pagamento', store: 'payment_terms', labels: ['name'] },
		{ index: 14, field: 'freight_type', label: 'Tipo Frete', types: [types.opt_freight_type(), types.datalist()], placeholder: '', title: 'Tipo de frete' },
		{ index: 15, field: 'freight_value', label: 'Valor Frete', types: [types.number(2)], placeholder: '0,00', title: 'Valor do frete' },
		{ index: 16, field: 'discount', label: '% Desconto', types: [types.number(2)], placeholder: '0,00', title: 'Percentual de desconto' },
		{ index: 17, field: 'value', label: 'Valor', types: [types.number(2)], placeholder: '0,00', title: 'Valor do pedido' },
		{ index: 18, field: 'notes', label: 'Observações', types: [], placeholder: '', title: 'Observações do pedido' },
		{ index: 19, field: 'book_observation', label: 'Obs.&nbsp;livro&nbsp;eletrônico', types: [], placeholder: '', title: 'Observação para livro eletrônico / NF-e' },
		{ index: 20, field: 'is_return', label: 'Devolução', types: [types.opt_bool(), types.datalist()], placeholder: '', title: 'Indicador de devolução' },
		{ index: 21, field: 'status', label: 'Status', types: [types.opt_output_order_status(), types.datalist()], placeholder: '', title: 'Status do pedido' },
		{ index: 25, field: 'expected_delivery_date', label: 'Prev.&nbsp;Entrega', types: [types.date()], placeholder: 'dd/mm/aaaa', title: 'Data de previsão de entrega do pedido' },
		{ index: 23, field: 'lines_sku_summary', label: 'SKU (itens)', types: [], placeholder: 'trecho do SKU', title: 'Busca nos SKUs / SKU parcial das linhas (operador Contém recomendado)' },
		{ index: 24, field: 'lines_product_summary', label: 'Produto (itens)', types: [], placeholder: 'nome do produto', title: 'Busca nos nomes dos produtos das linhas (operador Contém recomendado)' },
		{ index: 26, field: 'is_emergency', label: 'Emergencial', types: [types.opt_bool(), types.datalist()], placeholder: '', title: 'Pedido emergencial' },
		{ index: 40, field: 'pre_reserved_pending', label: 'Pré-reserva&nbsp;pendente', types: [types.number(0)], placeholder: '', title: 'Quantidade ainda comprometida por este pedido aprovado (não é reserva física)', computed: true },
		{ index: 41, field: 'simulated_available_quantity', label: 'Saldo&nbsp;livre&nbsp;simulado', types: [types.number(0)], placeholder: '', title: 'Saldo livre operacional menos pré-reservas de pedidos aprovados (pior SKU do pedido)', computed: true },
		{ index: 42, field: 'stock_situation', label: 'Situação&nbsp;estoque', types: [], placeholder: '', title: 'Situação do saldo simulado (OK / ATENÇÃO / negativo / sem livre)', computed: true },
	];

	let operations = [
		{ hash: '#create-outputs', label: 'Gerar&nbsp;saídas' },
		{ hash: '#upload', label: 'Upload&nbsp;planilha' },
		{ hash: '#approve-orders', label: 'Aprovar&nbsp;selecionados' },
		{ hash: '#create-pending-order', label: 'Gerar&nbsp;pedido&nbsp;complementar' },
		{ hash: '#mark-partial-completed', label: 'Concluir&nbsp;parcial' },
	];

	let reports = [
		{ hash: '#print-pending', 	label: 'Relatório&nbsp;de&nbsp;Pendências'	},
		{ hash: '#print-order-doc', label: 'Espelho&nbsp;de&nbsp;Pedido'	},
		{ hash: '#print-durability', label: 'Durabilidade&nbsp;de&nbsp;Estoque&nbsp;(Excel)' },
		{ hash: '#export-list-csv', label: 'Exportar&nbsp;listagem&nbsp;(CSV)'	},
	];

	router.set('#create-outputs', output_orders_crud.createOutputs.bind(output_orders_crud));

	router.set('#upload', output_orders_crud.uploadFromSpreadsheet.bind(output_orders_crud));

	router.set('#approve-orders', output_orders_crud.approveOrders.bind(output_orders_crud));
	router.set('#create-pending-order', output_orders_crud.createPendingOrder.bind(output_orders_crud));
	router.set('#mark-partial-completed', output_orders_crud.markPartialCompleted.bind(output_orders_crud));

	router.set('#print-pending', output_orders_crud.printPendingOrders.bind(output_orders_crud));
	router.set('#print-order-doc', output_orders_crud.printOrderDocument.bind(output_orders_crud));
	router.set('#print-durability', output_orders_crud.printDurability.bind(output_orders_crud));
	router.set('#export-list-csv', output_orders_crud.exportListCsv.bind(output_orders_crud));

	output_orders_crud.run(
		'output_orders',
		'Pedidos&nbsp;Saída',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		operations,
		reports
	);

};

export default outputOrders;

